import React, { useState, useEffect } from 'react';
import { logError } from '../lib/logger';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Download } from 'lucide-react';
import { trackEvent } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Gated download modal.
 * Collects Name, Company, Email + opt-in (DEFAULT CHECKED for gated flows),
 * records the lead on the backend, then opens the unlocked PDF URL in a new tab.
 *
 * Props:
 *   open, onOpenChange — dialog state
 *   resourceTitle      — human-readable title of the gated asset
 *   resourceUrl        — direct URL of the PDF to unlock
 */
const GatedDownloadModal = ({ open, onOpenChange, resourceTitle, resourceUrl }) => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    mailing_list_opt_in: true, // auto-checked for gated downloads
  });

  useEffect(() => {
    if (open) {
      setFormData({ name: '', company: '', email: '', mailing_list_opt_in: true });
    }
  }, [open]);

  const updateField = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resourceUrl) return;
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/leads/gated-download`, {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        resource_title: resourceTitle,
        resource_url: resourceUrl,
        mailing_list_opt_in: formData.mailing_list_opt_in,
      });
      trackEvent('gated_pdf_unlock', { resource: resourceTitle });
      toast.success('Download unlocked — opening your PDF now.');
      window.open(resourceUrl, '_blank', 'noopener,noreferrer');
      onOpenChange(false);
    } catch (err) {
      logError('Gated download failed', err);
      toast.error(
        err?.response?.data?.detail ||
          'We could not unlock the download right now. Please try again or contact info@ctrlsys.com.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#1e2d3b]">
            Unlock the PDF
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {resourceTitle
              ? `Tell us a bit about yourself to download “${resourceTitle}.”`
              : 'Tell us a bit about yourself to download this resource.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="gated-name">Full Name *</Label>
            <Input
              id="gated-name"
              data-testid="gated-name-input"
              required
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="John Doe"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gated-company">Company *</Label>
            <Input
              id="gated-company"
              data-testid="gated-company-input"
              required
              value={formData.company}
              onChange={(e) => updateField('company', e.target.value)}
              placeholder="Your Aviation Company"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gated-email">Email *</Label>
            <Input
              id="gated-email"
              type="email"
              data-testid="gated-email-input"
              required
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="john@company.com"
            />
          </div>

          <label className="flex items-start gap-3 rounded-lg bg-slate-50 p-3 border border-slate-200 cursor-pointer">
            <input
              type="checkbox"
              data-testid="gated-optin-checkbox"
              checked={formData.mailing_list_opt_in}
              onChange={(e) => updateField('mailing_list_opt_in', e.target.checked)}
              className="mt-1 h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
            />
            <span className="text-sm text-slate-700 leading-snug">
              Add me to the ctrl.aero mailing list for case studies, product updates, and
              aviation maintenance insights. You can unsubscribe any time.
            </span>
          </label>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="gated-submit-btn"
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-3 text-[15px] disabled:opacity-60"
          >
            {submitting ? (
              'Unlocking…'
            ) : (
              <>
                <Download className="mr-2 h-5 w-5" />
                Unlock &amp; Download PDF
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default GatedDownloadModal;
